import * as React from 'react';
import { useMemo } from 'react';
import { Plus, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RequirePermission } from '@klareospace/pharos-lib-front-utils';
import { VEHICLE_PROFILES } from '@klareospace/pharos-lib-types';
import {
  ActionBar,
  ActionBarDivider,
  ActionBarSpacer,
  Button,
  InfoPopover,
  Input,
  MultiSelect,
} from '@klareospace/pharos-lib-web-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface VehicleTemplatesActionBarProps {
  onAdd: () => void;
  onProfileChange: (values: string[]) => void;
  onSearchChange: (value: string) => void;
  onStatusChange: (values: string[]) => void;
  profileFilter: string[];
  searchValue: string;
  statusFilter: string[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUSES = ['active', 'archived'] as const;

// ─── Component ────────────────────────────────────────────────────────────────

export function VehicleTemplatesActionBar({
  onAdd,
  onProfileChange,
  onSearchChange,
  onStatusChange,
  profileFilter,
  searchValue,
  statusFilter,
}: VehicleTemplatesActionBarProps): React.JSX.Element {
  const { t } = useTranslation('settings');

  const profileOptions = useMemo(
    () =>
      Object.values(VEHICLE_PROFILES).map((profile) => ({
        label: t(`vehicleTemplates.profiles.${profile}`),
        value: profile,
      })),
    [t],
  );

  const statusOptions = useMemo(
    () =>
      STATUSES.map((status) => ({
        label: t(`vehicleTemplates.actionBar.filters.${status}`),
        value: status,
      })),
    [t],
  );

  const multiSelectCommonProps = useMemo(
    () => ({
      hideClear: true,
      hideClose: true,
      maxCount: 2,
      showLabelWhenFiltering: true,
    }),
    [],
  );

  return (
    <ActionBar>
      <div className="relative">
        <Search className="absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          aria-label={t('vehicleTemplates.actionBar.search.ariaLabel')}
          className="h-9 w-[200px] ps-9 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring focus-visible:outline-solid"
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('vehicleTemplates.actionBar.search.placeholder')}
          value={searchValue}
        />
      </div>
      <ActionBarDivider />
      <MultiSelect
        {...multiSelectCommonProps}
        className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
        label={t('vehicleTemplates.actionBar.filters.status')}
        onValueChange={onStatusChange}
        options={statusOptions}
        placeholder={t('vehicleTemplates.actionBar.filters.all')}
        searchable={false}
        defaultValue={statusFilter}
        variant="compact"
      />
      <MultiSelect
        {...multiSelectCommonProps}
        className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
        label={t('vehicleTemplates.actionBar.filters.profile')}
        onValueChange={onProfileChange}
        options={profileOptions}
        placeholder={t('vehicleTemplates.actionBar.filters.allProfiles')}
        searchable={false}
        defaultValue={profileFilter}
        variant="compact"
      />
      <ActionBarSpacer />
      <InfoPopover
        description={t('vehicleTemplates.actionBar.tooltip.body')}
        title={t('vehicleTemplates.actionBar.tooltip.title')}
      />
      <RequirePermission permission="vehicle-templates:manage">
        <Button
          className="h-9 gap-1.5 rounded-md px-3 text-sm shadow-sm hover:shadow-md"
          onClick={onAdd}
          variant="default"
        >
          <Plus className="h-4 w-4" />
          {t('vehicleTemplates.actionBar.addVehicleTemplate')}
        </Button>
      </RequirePermission>
    </ActionBar>
  );
}
